import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import FormatDate from "@/components/formatting/format-date";
import Spinner from "@/components/spinner";
import ITripPayment from "@/models/trip-payments/itrip-payment";
import * as tripPaymentsService from '@/services/trip-payments-service';
import * as configService from '@/services/config-service';
import ISelectOption from "@/models/generic/iselect-option";
import AddTripPayment from "./add";
import EditTripPayment from "./edit";
import ConfirmationModal from "@/components/modals/confirmation";
import FormatCurrency from "@/common/format-currency";

interface IProps {
    guestId?: number;
    tripId?: number;
    defaultNewDate?: Date;
    retreatTotal: number;
    updateTripPayments?: (tripPayments: ITripPayment[]) => void;
}

const ViewTripPayments: React.FC<IProps> = ({ guestId, tripId, defaultNewDate, updateTripPayments, retreatTotal }) => {
    
    const [ isLoading, setIsLoading ] = useState<boolean>(true);
    const [ isAddingNew, setIsAddingNew ] = useState<boolean>(false);
    
    const [ tripPayments, setTripPayments ] = useState<ITripPayment[]>([]);
    const [ paymentMethods, setPaymentMethods ] = useState<ISelectOption[]>([]);

    const [ totalPaid, setTotalPaid ] = useState<number>(0);
    const [ totalRemaining, setTotalRemaining ] = useState<number>(0);
    
    const [ editing, setEditing ] = useState(new Map());
    const [ deleteTarget, setDeleteTarget ] = useState<number>(0);
    const [ showConfirmationDeletemodal, setShowConfirmationDeletemodal ] = useState<boolean>(false);

    async function updateData() {
        try {
            const paymentMethods = configService.GetPaymentMethods();

            const temp = new Map();
            
            if (tripId) {
                const tripPayments = await tripPaymentsService.getTripPayments(tripId);
                
                let paid = 0;

                tripPayments.map(x => {
                    temp.set(x.id, false)
                    paid += x.amount;
    
                    return x;
                })
    
                setTripPayments(tripPayments);
                setTotalPaid(paid);
                setTotalRemaining(retreatTotal - paid);
            } else {
                setTripPayments([]);

                setTotalPaid(0);
                setTotalRemaining(retreatTotal);
            }

            setPaymentMethods(await paymentMethods);

            setEditing(temp);
            
        } catch {
            setIsLoading(false);
            toast.error("The guest payments couldn't be loaded.");
        }

        setIsLoading(false);
    }

    useEffect(() => {
        const fetchData = async () => {
            await updateData();
        }

        fetchData();
    }, [])

    useEffect(() => {
        const paid = tripPayments.reduce((total, current) => { return total + current.amount }, 0);

        setTotalRemaining(retreatTotal - paid);

    }, [retreatTotal, tripPayments]);

    async function closedAddingNew(requiresUpdate: boolean, tripPayment?: ITripPayment) {
        if (requiresUpdate) {
            await updateData();
        } else if (tripPayment) {
            const data = [...tripPayments, tripPayment];
            setTripPayments(data);
            
            const total = data.reduce((total, current) => { return total + current.amount }, 0); 
            setTotalPaid(total);
            setTotalRemaining(retreatTotal - total);

            if (updateTripPayments) {
                updateTripPayments(data);
            }
        }

        setIsAddingNew(false);
    }

    async function closeEdit(tripPayment: ITripPayment, requiresUpdate: boolean) {
        if (requiresUpdate) {
            await updateData();
        } else if (tripPayment.id < 0) {

            const payments = tripPayments.map(x => {
                if (x.id === tripPayment.id) {
                    x = {
                        ...tripPayment
                    } as ITripPayment;
                }

                return x;
            })

            setTripPayments(payments);

            const total = payments.reduce((total, current) => { return total + current.amount }, 0); 
            setTotalPaid(total);
            setTotalRemaining(retreatTotal - total);

            if (updateTripPayments) {
                updateTripPayments(payments);
            }
        }
        
        setEditing(new Map(editing.set(tripPayment.id, false)));
    }

    async function requestToDelete(id: number) {
        setDeleteTarget(id);
        setShowConfirmationDeletemodal(true);
    }

    async function receivedConfirmationDeleteRequest(wantsToDelete: boolean) {
        setShowConfirmationDeletemodal(false);
        
        if (wantsToDelete) {
            if (deleteTarget < 0) {
                const payments = tripPayments.filter(x => x.id != deleteTarget);
    
                setTripPayments(payments);

                const total = payments.reduce((total, current) => { return total + current.amount }, 0); 
                setTotalPaid(total);
                setTotalRemaining(retreatTotal - total);                

                return;
            }
            
            const remove = async () => {
                await tripPaymentsService.deleteTripPayment(deleteTarget);
            }
    
            await toast.promise(
                remove(),
                {
                    pending: "Removing guest payment",
                    success: "Removed the guest payment",
                    error: "Something went wrong whilst trying to remove the guest payment"
                }
            )

            updateData();
        }
        
        setDeleteTarget(0);
    }

    return (
        <Spinner isLoading={isLoading}>
            <div className="p-2 m-2">

                <div>
                    Paid: {FormatCurrency(totalPaid, "£", true)} • 
                    Remaining: {FormatCurrency(totalRemaining, "£", true)}
                </div>

                <table className="w-full table-fixed text-black">
                    <thead>
                        <tr className="bg-gray-300 text-left pl-2">
                            <th className="pl-2">Payment Date</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th className="w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {tripPayments.length === 0 && !isAddingNew && (
                            <tr className="bg-gray-100" >
                                <td colSpan={4} className="text-center py-1">
                                    This guest hasn't made any payments
                                </td>
                            </tr>
                        )}
                        {tripPayments.length > 0 && (
                            tripPayments.map(x => {

                                if (editing.get(x.id)) {
                                    return (
                                        <tr className="bg-gray-100 pl-2" key={x.id}>
                                            <EditTripPayment 
                                                tripPayment={x}
                                                paymentMethods={paymentMethods}
                                                close={(tripPayment: ITripPayment, requiresUpdate: boolean) => closeEdit(tripPayment, requiresUpdate)}
                                            />
                                        </tr>
                                    )
                                }
                                else {
                                    return (
                                        <tr className="bg-gray-100 pl-2" key={x.id}>
                                            <td className="pl-2">
                                                <FormatDate date={x.paymentDate} includeYear={true} />
                                            </td>
                                            <td>£{x.amount}</td>
                                            <td>{paymentMethods.find(y => y.id === x.methodId)?.name}</td>
                                            <td className="text-right">
                                                <button
                                                    title="Edit"
                                                    className="bg-green-500 hover:brightness-110 text-white px-1 w-6"
                                                    onClick={() => setEditing(new Map(editing.set(x.id, true)))} 
                                                >
                                                    <i className="far fa-edit" />
                                                </button>
                                                <button className="mx-1 bg-red-600 hover:brightness-110 text-white px-1 w-6" onClick={() => requestToDelete(x.id)}>
                                                    <i className="far fa-trash-alt" />
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                }
                            })
                        )}

                        {!isAddingNew && (
                            <tr className="bg-gray-100" >
                                <td colSpan={4} className="text-center py-1">
                                    <button
                                        className="rounded text-sm px-3 py-1 ml-2 bg-blue-800 hover:brightness-110 text-white"
                                        onClick={() => setIsAddingNew(true)}
                                    >
                                            Add a new payment
                                    </button>
                                </td>
                            </tr>
                        )}
                        {isAddingNew && (
                            <AddTripPayment
                                guestId={guestId}
                                tripId={tripId}
                                paymentMethods={paymentMethods}
                                defaultNewDate={tripPayments.length === 0 ? defaultNewDate : undefined}
                                close={(requiresUpdate: boolean, tripPayment?: ITripPayment) => closedAddingNew(requiresUpdate, tripPayment)}
                            />
                        )}
                    </tbody>
                </table>
                {showConfirmationDeletemodal && <ConfirmationModal confirmationText="Are you sure you want to delete this guest payment?" closeModal={(response) => receivedConfirmationDeleteRequest(response)} />}
            </div>
        </Spinner>
    )
}

export default ViewTripPayments;